import React from 'react';

import { Root } from '@atlaskit/navigation-system/layout/root';
import { usePanelContext } from '@atlassian/entry-point-panel/use-panel-context';
import { defineMessage, useIntl } from 'react-intl';

import { GoalCapabilitiesProvider } from '@townsquare/goal-capabilities';
import { GriffinTopNavigation } from '@townsquare/griffin-navigation';
import { GlobalAppShortcutsProvider } from '@townsquare/griffin-navigation/global-navigation-context';
import { NavPlatformSideNav } from '@townsquare/nav-platform-integration/NavPlatformSideNav';
import { AppHomeWrapper } from '@townsquare/navigation/app-home-wrapper';
import { IntroPopup } from '@townsquare/onboarding/intro-popup';
import { ProjectCapabilitiesProvider } from '@townsquare/project-capabilities';
import { SIDEBAR_PANEL } from '@townsquare/sidebar-panel/panels';
import { usePreloadedEntryPointPanel } from '@townsquare/sidebar-panel-utils/hooks/use-preloaded-entry-point-panel';
import { SiteBannerProvider } from '@townsquare/site-banner';
import { fg } from '@townsquare/stat-sig/gate';
import { useNoWorkspaceExperience } from '@townsquare/workspace-store';

import { ProductHome } from '../griffin-navigation/ProductHome';
import { useSideNavAppItemsForNavPlatform } from '../griffin-navigation/use-side-nav-app-items';
import { useSideNavItems } from '../griffin-navigation/use-side-nav-items';
import { useUniversalCreateItems } from '../griffin-navigation/use-universal-create-items';
import { GriffinLayout } from '../layouts/Home/GriffinLayout';

export function GriffinRoute(): React.JSX.Element {
	return (
		<Root
			isSideNavShortcutEnabled={
				fg('navx-full-height-sidebar') || fg('platform-dst-keep-desired-fhs-features')
			}
		>
			<SiteBannerProvider postOfficeBanners={[]}>
				<GlobalAppShortcutsProvider>
					<GriffinRouteComponent />
				</GlobalAppShortcutsProvider>
			</SiteBannerProvider>
		</Root>
	);
}

const GriffinTopSection = () => {
	const noWorkspaceExperience = useNoWorkspaceExperience();
	const { items: universalCreateItems, compact } = useUniversalCreateItems();

	return (
		<GriffinTopNavigation
			renderProductHome={() => (
				<AppHomeWrapper>
					<ProductHome />
				</AppHomeWrapper>
			)}
			universalCreateItems={universalCreateItems}
			compact={compact}
			hideSideNavToggleButton={noWorkspaceExperience}
		/>
	);
};

function GriffinRouteComponent() {
	const noWorkspaceExperience = useNoWorkspaceExperience();

	const hideSideNav = noWorkspaceExperience;

	const preloadedPanel = usePreloadedEntryPointPanel();
	const { Panel: SidebarPanel, Provider: SidebarPanelContext } = usePanelContext(
		SIDEBAR_PANEL,
		preloadedPanel,
	);

	return (
		<>
			<SidebarPanelContext>
				<GoalCapabilitiesProvider>
					<ProjectCapabilitiesProvider>
						<GriffinTopSection />
						{!hideSideNav && <PlatformSideNav />}
					</ProjectCapabilitiesProvider>
				</GoalCapabilitiesProvider>
				<GriffinLayout PlatformSidebarPanel={SidebarPanel} />
			</SidebarPanelContext>
			{noWorkspaceExperience && <IntroPopup />}
		</>
	);
}

function PlatformSideNav() {
	const sidebarItems = useSideNavItems();
	const appShortcuts = useSideNavAppItemsForNavPlatform();
	const { formatMessage } = useIntl();

	// Goals nav-customisation is being disabled until it has it's own siteID/activationId
	const customizationStore = 'local';
	const disableMenuCustomization = true;

	return (
		<NavPlatformSideNav
			app="goals"
			appLabel={formatMessage(goalsAppLabelMessage)}
			appItems={sidebarItems}
			appShortcuts={appShortcuts}
			customizationStore={customizationStore}
			disableMenuCustomization={disableMenuCustomization}
		/>
	);
}

const goalsAppLabelMessage = defineMessage({
	id: 'townsquare.goals.app.label',
	defaultMessage: 'Goals',
	description: 'Label for the app when shown in the global side navigation.',
});
